import type { EntityData } from "@models/data/entityData";
import { SKYBOX_MODE } from "@models/data/skyboxData";
import type { TransferBytes } from "./IProjectModelSlim";
import type { ProjectModel } from "./ProjectModel";

/**
 * `ProjectModelSceneLoad` — partial of {@link ProjectModel} owning the scene
 * LOAD lifecycle: the smooth-transition predicate that decides whether a
 * scene switch gets a loading screen, and the progress session that drives
 * that screen.
 *
 * Port of two Unity seams that the web port previously had no equivalent for:
 *
 *  - `ProjectModelSlim.IsSmoothTransitionSupported`
 *    (`Models/ProjectModel/ProjectModelSlim.cs:640-683`), and
 *  - the branch it gates in `AsyncNavigationBaseModel.NavigateToSceneAsync`
 *    (`Models/AsyncNavigationBaseModel.cs:661-726`):
 *
 *      A. `IsSceneLoaded(scene)`                    → activate, no UI
 *      B. `IsSmoothTransitionSupported(from, to)`   → load silently, no UI
 *      C. otherwise                                 → `LoadSceneProgress` view
 *
 * Two things that are easy to conflate and must stay separate:
 *
 *  - The **navigation** gate (loading screen yes/no) is
 *    {@link ProjectModelSceneLoad.isSmoothTransitionSupported} ALONE. It is
 *    duration-independent — Unity never consults `SceneTransitionSpeed` here.
 *  - The **animation** gate (morph the shared parts yes/no) additionally
 *    requires `sceneTransitionDurationSeconds(to.transitionSpeed) > 0` and a
 *    non-empty paired-track set. That one lives in `SceneEntityRenderer`
 *    (Unity's `ScenesTransitionModel.OnActiveSceneChanged`).
 *
 * ### Residency
 *
 * Unity's `IsSceneLoaded` reads `State.LoadedScenes` — scenes whose CONTENT
 * is instantiated, not scenes that exist in the project. The web tracks the
 * same thing in `m_residentSceneIds`: a scene becomes resident once its
 * Dynamo rows are fetched AND the renderer has finished building it. Web
 * deviation worth knowing: unlike Unity, the web never unloads the previous
 * scene (`SceneEntityRenderer` keeps every visited scene parented and just
 * toggles `visible`), so residency only ever grows within a project. That is
 * pre-existing behaviour and is what makes a revisit instant.
 *
 * ### Progress
 *
 * A web scene load has two latency phases, neither of which maps 1:1 onto
 * Unity's six milestones (`ProjectModelSlim.cs:828-833`), so they are mapped
 * by phase weight instead:
 *
 *   0.00 → 0.50   data phase   — Dynamo snapshots/settings/parts/materials
 *   0.50 → 0.90   build phase  — `SceneEntityRenderer` glTF downloads,
 *                                byte-weighted per placement (task 0013)
 *   1.00          complete
 *
 * The same downloads are ALSO reported in bytes and ride every progress
 * event, because a percentage cannot distinguish a slow link from a hung one
 * — see {@link SceneLoadSession.bytes}. That readout is web-only; Unity's
 * progress views carry a milestone name where this one carries a size.
 *
 * The build phase is PUSHED in by the renderer through
 * `IProjectModel.reportSceneBuild*` (the inverse-direction sibling of
 * `IPreviewCaptureHost`: the model owns *when*, the viewport owns the
 * Three.js work), so this model never imports Three.js.
 *
 * ### The build host
 *
 * Whether anything CAN report is known from the viewport's
 * `IPreviewCaptureHost` registration: the viewport attaches it and mounts the
 * renderer in one `onMounted`, and detaches it on unmount. A session handed
 * over before that attach — a project opened from the grid, which pushes the
 * editor route only once the project is active — waits for the host instead of
 * running the "nobody is listening" grace, because nobody is listening YET.
 * See {@link ProjectModelSceneLoad.armSceneBuildWatch}.
 */

/** Open scene-load session. At most one at a time, newest wins. */
export interface SceneLoadSession {
  readonly sceneId: string;
  readonly fromSceneId: string | null;
  /** Unity branch B — the caller must NOT show a loading screen. */
  readonly isSmoothTransition: boolean;
  /** False for branch B and for the project-open load (see `beginSceneLoad`). */
  readonly showProgressUi: boolean;
  /** Matches `m_setActiveSceneSeq` at open time; a newer navigation wins. */
  readonly seq: number;
  dataProgress01: number;
  buildProgress01: number;
  /** Highest value emitted so far — Unity's bar only ever moves forward. */
  emitted01: number;
  /**
   * Last byte counts the renderer reported, replayed on EVERY later emission
   * of this session including the terminal one. Sticky on purpose: the
   * download finishes well before the scene is on screen (parse and Draco
   * decode report nothing), and dropping the label at that moment would read
   * as the transfer having been abandoned rather than completed.
   */
  bytes: TransferBytes | null;
  sawBuildReport: boolean;
  /**
   * Renderer builds currently running for this scene (`reportSceneBuildStarted`
   * increments; a terminal report — finished / aborted / failed — settles it).
   * While non-zero the watchdog re-arms instead of completing, so "30 s with
   * no report" must mean "30 s with nothing running", not "30 s of work". The
   * download itself now heartbeats (task 0013), but the silent bands survive
   * it: glTF parse and Draco decode report nothing at all, and a transfer
   * whose response carried no `Content-Length` is indeterminate.
   */
  openBuildCount: number;
  /**
   * The build watch was armed while no build host (the viewport) was attached,
   * so the watchdog is the host wait, not the grace. Cleared when the host
   * attaches ({@link ProjectModelSceneLoad.noteSceneBuildHostAttached}).
   */
  awaitingBuildHost: boolean;
  timer: number | null;
}

export class ProjectModelSceneLoad {
  /**
   * Port of `ProjectModelSlim.IsSmoothTransitionSupported`
   * (`ProjectModelSlim.cs:640-683`). Evaluated in Unity's exact order, with
   * Unity's short-circuits preserved.
   *
   * `true` means "everything the target scene needs is already in front of
   * the user", so the switch can happen in place with no loading screen.
   */
  isSmoothTransitionSupported(
    this: ProjectModel,
    fromSceneId: string | null,
    toSceneId: string,
  ): boolean {
    // Unity: `if (fromScene == null) return false;` — the first scene of a
    // freshly opened project therefore always takes the loading-screen branch.
    if (!fromSceneId || fromSceneId === toSceneId) return false;
    const from = this.getScene(fromSceneId);
    const to = this.getScene(toSceneId);
    if (!from || !to) return false;

    // A custom skybox is a hard short-circuit in Unity: neither the asset
    // check nor the collision check runs in either outcome. The target is
    // smooth only when the SAME custom texture is already on screen.
    if (to.skybox.mode === SKYBOX_MODE.Custom) {
      return (
        from.skybox.mode === SKYBOX_MODE.Custom &&
        from.skybox.textureId.length > 0 &&
        to.skybox.textureId === from.skybox.textureId
      );
    }

    // Unity `SceneInfo.IsEmpty => EntitiesCount == 0` where
    // `EntitiesCount => AssetIDs.Count`. Deliberately NOT `entityCount` —
    // `mapSceneDtoToInfo` hardcodes that to 0 (`sceneDto.ts:144`).
    if (to.assetIds.length === 0) return true;

    // Every asset the target needs must already be instantiated in the
    // outgoing scene, so nothing has to be imported. Extras in `from` are
    // fine; Unity compares set membership, not multisets.
    const fromAssetIds = new Set(from.assetIds);
    for (const assetId of to.assetIds) {
      if (!fromAssetIds.has(assetId)) return false;
    }

    // Unity vetoes the smooth path when the OUTGOING scene has physics live
    // (`ScenesTransitionModel` checks both scenes; `IsSmoothTransitionSupported`
    // checks only `from`). The web has no physics, but the flag round-trips
    // from Dynamo, so honouring it keeps the branch decision identical to the
    // desktop client for the same project data.
    if (hasCollisionEnabledEntity(this.getEntities(fromSceneId))) return false;

    return true;
  }

  /**
   * Opens a scene-load session and emits `onBeginLoadScene`.
   *
   * Mirrors Unity, where `LoadSceneAsync` early-returns for an already-loaded
   * scene so `BeginLoadScene` never fires (`ProjectModelSlim.cs:689-692`) —
   * callers must skip this when {@link ProjectModel.isSceneLoaded} is true.
   *
   * The event fires for BOTH the smooth and the progress-screen branch (Unity
   * raises it inside `LoadSceneAsync`, which both branches call); it is
   * `showProgressUi` that tells the UI whether to render chrome. That flag is
   * false for branch B and also for the project-open load, where
   * `LoadProjectProgressView` already owns the screen.
   */
  beginSceneLoad(
    this: ProjectModel,
    sceneId: string,
    fromSceneId: string | null,
    options: { isSmoothTransition: boolean; showProgressUi?: boolean },
  ): void {
    const state = this as unknown as SceneLoadInternals;
    this.abandonSceneLoad();
    const isSmoothTransition = options.isSmoothTransition;
    const showProgressUi = options.showProgressUi ?? !isSmoothTransition;
    state.m_sceneLoadSession = {
      sceneId,
      fromSceneId,
      isSmoothTransition,
      showProgressUi,
      seq: state.m_setActiveSceneSeq,
      dataProgress01: 0,
      buildProgress01: 0,
      emitted01: 0,
      bytes: null,
      sawBuildReport: false,
      openBuildCount: 0,
      awaitingBuildHost: false,
      timer: null,
    };
    this.onBeginLoadScene.emit({
      sceneId,
      fromSceneId,
      isSmoothTransition,
      showProgressUi,
    });
    this.onLoadSceneProgress.emit({ sceneId, progress01: 0 });
  }

  /** Data-phase input, 0…1 (see the phase table in the file header). */
  reportSceneDataProgress(this: ProjectModel, sceneId: string, progress01: number): void {
    const session = sessionFor(this, sceneId);
    if (!session) return;
    session.dataProgress01 = clamp01(progress01);
    emitSceneProgress(this, session);
  }

  /**
   * Hands the session over to the renderer. Called once the data phase is
   * done and `onActiveSceneChanged` has been emitted, so a build (if any) is
   * already queued.
   *
   * Arms one of three watchdogs, so the overlay can never wedge:
   *
   * - a build has already reported → the long silence ceiling;
   * - a build host is attached but nothing reported → the short grace,
   *   because a mounted renderer reports inside the `onActiveSceneChanged`
   *   emit, so its silence means it has nothing to build;
   * - NO host is attached → the host wait. The renderer may not exist YET:
   *   opening a project from the grid pushes the editor route on
   *   `onActiveProjectChanged`, so the viewport mounts a lazy-loaded route
   *   later — measured at 3.1 s on the dev server's first editor visit — and
   *   a 400 ms grace closed the session before it arrived, dismissing the
   *   project overlay over an unbuilt scene. The attach restarts the grace
   *   ({@link noteSceneBuildHostAttached}); a host that never comes (headless,
   *   a handover that never lands) is bounded by the wait itself.
   */
  armSceneBuildWatch(this: ProjectModel, sceneId: string): void {
    const session = sessionFor(this, sceneId);
    if (!session) return;
    session.awaitingBuildHost = !session.sawBuildReport && !hasSceneBuildHost(this);
    armWatchdog(this, session, buildWatchDelayMs(session));
  }

  /**
   * The viewport attached its host (`attachPreviewCaptureHost`), so a renderer
   * now exists. A session that was waiting for it restarts the grace from
   * THIS moment, the way it would have run had the viewport been mounted when
   * the watch was armed. In practice the renderer's `reportSceneBuildStarted`
   * lands in the same `onMounted`, and the open build then holds the session
   * until the scene is on screen.
   */
  noteSceneBuildHostAttached(this: ProjectModel): void {
    const session = (this as unknown as SceneLoadInternals).m_sceneLoadSession;
    if (!session?.awaitingBuildHost) return;
    session.awaitingBuildHost = false;
    armWatchdog(this, session, buildWatchDelayMs(session));
  }

  /**
   * Renderer push seam — a build (one `activateScene` run) started for this
   * scene. Every start is balanced by exactly one terminal report: finished,
   * aborted, or failed. The open-build count is what keeps the watchdog from
   * completing the session mid-build — parse and decode emit nothing at all,
   * so report silence alone must not mean the build is gone (see the count's
   * doc on {@link SceneLoadSession}).
   *
   * Kept on the SESSION, not the renderer: the renderer can be disposed and
   * recreated mid-load (task 0012), which would zero a renderer-side count
   * while its replacement's build is still running.
   */
  reportSceneBuildStarted(this: ProjectModel, sceneId: string): void {
    const session = sessionFor(this, sceneId);
    if (!session) return;
    session.openBuildCount++;
    // A build exists, so `armSceneBuildWatch` must never pick the short
    // "nobody is listening" grace for this session.
    session.sawBuildReport = true;
  }

  /**
   * Renderer push seam — glTF placement progress for this scene.
   *
   * `completed` is FRACTIONAL: the renderer weights each placement by its own
   * download (`sceneEntityRenderer.ensureSceneRendered`), so a one-model scene
   * animates through its transfer instead of jumping 0 → 1. `total` stays the
   * placement count, so the ratio is unchanged for whole values.
   *
   * `bytes` is the same downloads counted in the other currency, for the
   * readout beside the bar. It is deliberately NOT derived from `completed`:
   * that fraction also covers parse and instantiation, and it is per
   * PLACEMENT while bytes are per ASSET (a model placed twice downloads
   * once). Absent or `null` leaves the last report standing — see
   * {@link SceneLoadSession.bytes}.
   */
  reportSceneBuildProgress(
    this: ProjectModel,
    sceneId: string,
    completed: number,
    total: number,
    bytes?: TransferBytes | null,
  ): void {
    const session = sessionFor(this, sceneId);
    if (!session) return;
    session.sawBuildReport = true;
    session.buildProgress01 = total > 0 ? clamp01(completed / total) : 1;
    if (bytes && bytes.total > 0) session.bytes = bytes;
    emitSceneProgress(this, session);
    armWatchdog(this, session, K_SCENE_BUILD_TIMEOUT_MS);
  }

  /**
   * Renderer push seam — the scene is on screen.
   *
   * With no session open this is normally a rebuild of a scene that is
   * already resident, and does nothing. The exception is a scene whose session
   * a watchdog closed before any renderer reported. Since the build-host wait,
   * that is a viewport that took longer than {@link K_SCENE_BUILD_HOST_WAIT_MS}
   * to mount (a cold editor chunk on a slow link), or a host with no renderer
   * behind it. Its build then runs with no session, and without this the scene
   * was never marked resident — every later visit to it raised the loading
   * overlay over content that was already built. A build that really finished
   * is exactly the evidence the watchdog lacked, so it grants the residency
   * here. A FAILED load is never recorded, so its retry still loads.
   */
  reportSceneBuildFinished(this: ProjectModel, sceneId: string): void {
    const session = sessionFor(this, sceneId);
    if (!session) {
      const state = this as unknown as SceneLoadInternals;
      if (state.m_scenesAwaitingLateBuild.delete(sceneId)) {
        state.m_residentSceneIds.add(sceneId);
      }
      return;
    }
    session.sawBuildReport = true;
    session.buildProgress01 = 1;
    this.completeSceneLoad(sceneId);
  }

  /**
   * Renderer push seam — a build was superseded (generation bump) or the
   * renderer was disposed mid-build. A replacement build normally starts in
   * the same tick, so re-arm the short grace watchdog rather than completing
   * or failing: if no replacement arrives, the watchdog closes the session.
   * When ANOTHER build for this scene is still open (the common project-open
   * case — the superseded first build settles while its replacement is mid-
   * download), the current watchdog is left alone: shortening it to the grace
   * window on every abort is what let a live download be cut off at 400 ms.
   */
  reportSceneBuildAborted(this: ProjectModel, sceneId: string): void {
    const session = sessionFor(this, sceneId);
    if (!session) return;
    session.openBuildCount = Math.max(0, session.openBuildCount - 1);
    if (session.openBuildCount === 0) {
      armWatchdog(this, session, K_SCENE_BUILD_GRACE_MS);
    }
  }

  /** Renderer push seam — the build threw. */
  reportSceneBuildFailed(this: ProjectModel, sceneId: string, error: Error): void {
    if (!sessionFor(this, sceneId)) return;
    this.failSceneLoad(sceneId, error);
  }

  /**
   * Emits progress `1`, then `onLoadSceneCompleted`, and — unless the caller
   * says otherwise — marks the scene resident.
   *
   * `grantResidency: false` is the watchdog's path: a load that TIMED OUT did
   * not finish, and residency is permanent for the tab's life (`isSceneLoaded`
   * short-circuits every later visit to branch A), so a timeout-granted
   * residency poisons the scene — it can never load again. Unity has no
   * timeout at all; it awaits real completion, so only real completion may
   * grant — which is why a late build that DOES finish still can, through
   * `reportSceneBuildFinished`.
   */
  completeSceneLoad(
    this: ProjectModel,
    sceneId: string,
    options?: { grantResidency?: boolean },
  ): void {
    const state = this as unknown as SceneLoadInternals;
    const session = sessionFor(this, sceneId);
    if (!session) return;
    clearWatchdog(session);
    state.m_sceneLoadSession = null;
    if (options?.grantResidency !== false) {
      state.m_residentSceneIds.add(sceneId);
      state.m_scenesAwaitingLateBuild.delete(sceneId);
    }
    // Carries the session's last byte report so the readout finishes on
    // "492 of 492 MB" rather than blanking as the bar fills.
    this.onLoadSceneProgress.emit({ sceneId, progress01: 1, bytes: session.bytes });
    this.onLoadSceneCompleted.emit({ sceneId });
  }

  /** Closes the session without marking residency, so a retry re-loads. */
  failSceneLoad(this: ProjectModel, sceneId: string, error: Error): void {
    const state = this as unknown as SceneLoadInternals;
    const session = sessionFor(this, sceneId);
    if (!session) return;
    clearWatchdog(session);
    state.m_sceneLoadSession = null;
    this.onLoadSceneFailed.emit({ sceneId, error });
  }

  /**
   * Drops the open session (superseded navigation, project unload, route
   * leave, load cancel). Emits neither `onLoadSceneCompleted` nor
   * `onLoadSceneFailed` — a stuck full-screen overlay is the failure mode
   * this exists to prevent — but it DOES emit `onLoadSceneAbandoned`, so an
   * await keyed on the session ({@link waitForSceneLoadAsync}) settles
   * instead of hanging forever. Abandonment is terminal for the session; it
   * is not a failure of the load.
   */
  abandonSceneLoad(this: ProjectModel): void {
    const state = this as unknown as SceneLoadInternals;
    const session = state.m_sceneLoadSession;
    if (!session) return;
    clearWatchdog(session);
    state.m_sceneLoadSession = null;
    this.onLoadSceneAbandoned.emit({ sceneId: session.sceneId });
  }

  /**
   * Resolves when the OPEN session for `sceneId` closes, whichever way it
   * closes. No open session for that scene → resolves immediately (the load
   * already finished, failed, or never opened — all states the caller can
   * proceed from). `loadProjectAsync` awaits this so the project overlay
   * covers the renderer build (the S3 GLB downloads) instead of dismissing
   * at the START of the transfer (task 0011).
   *
   * Settlement map — only an ABORT rejects:
   * - completed → resolve (the scene is on screen);
   * - failed    → resolve. A scene-load failure is already non-fatal for
   *   project open (the data-phase failure path completes the project with a
   *   toast + retry affordance); making the build half fatal would bounce the
   *   user to the projects grid over a render hiccup;
   * - abandoned → resolve. The session was superseded (presenter follow, a
   *   Spark navigation, a route leave) — whatever superseded it owns the
   *   screen now;
   * - `signal` aborts → reject with the abort reason, so the caller's catch
   *   runs its failure path (Cancel must actually cancel);
   * - waiter-owned ceiling → resolve with a warning. Deliberately NOT the
   *   session's own watchdog: this is the last-resort backstop for a session
   *   whose events were lost, generous enough that a slow real download can
   *   never hit it before the watchdog machinery does its job.
   *
   * `onProgress` receives the session's raw progress (0…1) per emission, plus
   * the byte counts behind it; the caller owns any rescaling onto its own bar
   * and forwards the bytes untouched (they are already absolute).
   */
  waitForSceneLoadAsync(
    this: ProjectModel,
    sceneId: string,
    signal?: AbortSignal,
    onProgress?: (progress01: number, bytes: TransferBytes | null) => void,
  ): Promise<void> {
    const state = this as unknown as SceneLoadInternals;
    const session = state.m_sceneLoadSession;
    if (!session || session.sceneId !== sceneId) return Promise.resolve();
    if (signal?.aborted) return Promise.reject(abortErrorOf(signal));

    return new Promise<void>((resolve, reject) => {
      const offs: Array<() => void> = [];
      let ceiling: number | null = null;
      const cleanup = (): void => {
        for (const off of offs) off();
        offs.length = 0;
        if (ceiling !== null) window.clearTimeout(ceiling);
        ceiling = null;
        if (signal) signal.removeEventListener("abort", onAbort);
      };
      const settle = (): void => {
        cleanup();
        resolve();
      };
      const onAbort = (): void => {
        cleanup();
        reject(abortErrorOf(signal!));
      };
      offs.push(
        this.onLoadSceneProgress.on(({ sceneId: id, progress01, bytes }) => {
          if (id === sceneId) onProgress?.(progress01, bytes ?? null);
        }),
        this.onLoadSceneCompleted.on(({ sceneId: id }) => {
          if (id === sceneId) settle();
        }),
        this.onLoadSceneFailed.on(({ sceneId: id }) => {
          if (id === sceneId) settle();
        }),
        this.onLoadSceneAbandoned.on(({ sceneId: id }) => {
          if (id === sceneId) settle();
        }),
      );
      if (signal) signal.addEventListener("abort", onAbort, { once: true });
      ceiling = window.setTimeout(() => {
        ceiling = null;
        console.warn(
          `[ProjectModel] waitForSceneLoadAsync ceiling hit for scene=${sceneId} — proceeding without the build`,
        );
        settle();
      }, K_SCENE_LOAD_WAIT_CEILING_MS);
    });
  }
}

/**
 * A build host is attached but no renderer report arrived within this window
 * → complete. Only safe once a host exists: a mounted renderer reports inside
 * the `onActiveSceneChanged` emit, so its silence means it has nothing to
 * build. Before a host exists, {@link K_SCENE_BUILD_HOST_WAIT_MS} applies.
 */
const K_SCENE_BUILD_GRACE_MS = 400;
/**
 * The watch was armed with NO build host attached → how long to wait for the
 * viewport to mount before closing the session without it. Covers the grid
 * handover, where the editor route (a 1.5 MB lazy chunk) mounts after the
 * scene is active: 3.1 s measured on the dev server's first editor visit,
 * 0.25–0.29 s on a production build with the chunk already cached, and far
 * more for a cold chunk on a slow link or a phone CPU. Also the bound for a
 * host that never arrives — headless, or a handover that never lands — so it
 * must stay finite; the overlay's Cancel works throughout.
 */
const K_SCENE_BUILD_HOST_WAIT_MS = 15_000;
/**
 * Silence ceiling once the renderer HAS started reporting. Fires only when
 * no build is open (see `armWatchdog`) — a session with a build running
 * re-arms instead, because a build sitting in glTF parse or Draco decode
 * legitimately reports nothing.
 */
const K_SCENE_BUILD_TIMEOUT_MS = 30_000;
/**
 * `waitForSceneLoadAsync`'s own backstop, deliberately NOT the session
 * watchdog: the watchdog re-arms while builds are open, so a session whose
 * terminal events were somehow lost could hold `loadProjectAsync` forever.
 * Generous — Unity awaits real completion with no ceiling at all, and a
 * large model on a slow link takes minutes.
 */
const K_SCENE_LOAD_WAIT_CEILING_MS = 300_000;

/** Protected `ProjectModel` state this partial reaches (mixin-cast pattern). */
interface SceneLoadInternals {
  m_sceneLoadSession: SceneLoadSession | null;
  m_setActiveSceneSeq: number;
  readonly m_residentSceneIds: Set<string>;
  readonly m_scenesAwaitingLateBuild: Set<string>;
}

/** The open session iff it belongs to `sceneId` (the id-guard every seam shares). */
function sessionFor(model: ProjectModel, sceneId: string): SceneLoadSession | null {
  const session = (model as unknown as SceneLoadInternals).m_sceneLoadSession;
  return session && session.sceneId === sceneId ? session : null;
}

/** True while the viewport is mounted (see "The build host" in the file header). */
function hasSceneBuildHost(model: ProjectModel): boolean {
  return (model as unknown as { m_previewCaptureHost: unknown }).m_previewCaptureHost != null;
}

/**
 * The watchdog a session handed to the renderer should run right now. A build
 * that has reported keeps the long ceiling: `onActiveSceneChanged` is emitted
 * synchronously just before `armSceneBuildWatch` and the renderer's first
 * report lands inside that emit, so downgrading it to the grace would cut a
 * real load off at 400 ms with the overlay dismissed over an unbuilt scene.
 */
function buildWatchDelayMs(session: SceneLoadSession): number {
  if (session.sawBuildReport) return K_SCENE_BUILD_TIMEOUT_MS;
  return session.awaitingBuildHost ? K_SCENE_BUILD_HOST_WAIT_MS : K_SCENE_BUILD_GRACE_MS;
}

function emitSceneProgress(model: ProjectModel, session: SceneLoadSession): void {
  const raw = session.dataProgress01 * K_DATA_PHASE_WEIGHT
    + session.buildProgress01 * K_BUILD_PHASE_WEIGHT;
  // Unity's `ProgressViewModel.Update` only ever eases UPWARD
  // (`ProgressViewModel.cs:108-115`), so never report a regression.
  if (raw <= session.emitted01) return;
  session.emitted01 = raw;
  model.onLoadSceneProgress.emit({
    sceneId: session.sceneId,
    progress01: raw,
    bytes: session.bytes,
  });
}

const K_DATA_PHASE_WEIGHT = 0.5;
const K_BUILD_PHASE_WEIGHT = 0.4;

function armWatchdog(model: ProjectModel, session: SceneLoadSession, delayMs: number): void {
  clearWatchdog(session);
  session.timer = window.setTimeout(() => {
    session.timer = null;
    if (sessionFor(model, session.sceneId) !== session) return;
    // A build is still running — parse and decode report nothing, so
    // silence is expected. Re-arm rather than cutting the load off; the
    // build's own settlement (finished / aborted / failed) closes the
    // session, and a wedged fetch is bounded by the cache aborting its
    // transfers on clear/dispose plus the waiter's own ceiling.
    if (session.openBuildCount > 0) {
      armWatchdog(model, session, K_SCENE_BUILD_TIMEOUT_MS);
      return;
    }
    // Nothing is building and nothing else is coming — close the session so
    // the overlay can never wedge. WITHOUT residency: this load did not
    // actually finish, and residency is permanent (see `completeSceneLoad`).
    if (session.awaitingBuildHost && !session.sawBuildReport) {
      console.warn(
        `[ProjectModel] no viewport attached within ${K_SCENE_BUILD_HOST_WAIT_MS} ms for scene=${session.sceneId} — closing its load session without the build`,
      );
    }
    // A renderer that mounts even later than that can still finish it, and
    // `reportSceneBuildFinished` grants the residency then — but only when no
    // renderer ever reported, since a build that started and then went quiet
    // already had its chance to finish inside this session.
    if (!session.sawBuildReport) {
      (model as unknown as SceneLoadInternals).m_scenesAwaitingLateBuild.add(session.sceneId);
    }
    model.completeSceneLoad(session.sceneId, { grantResidency: false });
  }, delayMs);
}

/** The abort reason as an `Error` (browsers hand back a DOMException). */
function abortErrorOf(signal: AbortSignal): Error {
  const reason: unknown = signal.reason;
  return reason instanceof Error ? reason : new Error("Aborted");
}

function clearWatchdog(session: SceneLoadSession): void {
  if (session.timer === null) return;
  window.clearTimeout(session.timer);
  session.timer = null;
}

/** Unity walks `GetEntities(fromScene)` and tests each snapshot's flag. */
function hasCollisionEnabledEntity(entities: ReadonlyArray<EntityData>): boolean {
  for (const entity of entities) {
    if (anySnapshotHasCollision(entity.snapshots)) return true;
  }
  return false;
}

function anySnapshotHasCollision(
  snapshots: ReadonlyArray<{
    readonly isCollisionEnabled: boolean;
    readonly children: ReadonlyArray<unknown>;
  }>,
): boolean {
  for (const snapshot of snapshots) {
    if (snapshot.isCollisionEnabled) return true;
    if (
      anySnapshotHasCollision(
        snapshot.children as ReadonlyArray<{
          readonly isCollisionEnabled: boolean;
          readonly children: ReadonlyArray<unknown>;
        }>,
      )
    ) {
      return true;
    }
  }
  return false;
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
